// ═══════════════════════════════════════════════════════════════════════════
// PanneauDisque.jsx — Stockage & Disque — PesacoreIH
// Store : systemMetrics.disk (gauge temps réel via socket)
// Backend : /superadmin/disk (détail collections + dossiers + logs)
// ═══════════════════════════════════════════════════════════════════════════

import {
    AlertTriangle, Database, FileText, FolderOpen,
    RefreshCw, Server, Trash2, X,
} from 'lucide-react';
import { useEffect, useState } from 'react';
import api from '../../config/axiosConfig';
import usePesacoreIH from '../../stores/usePesacore';

// ─── Helpers ──────────────────────────────────────────────────────────────

function fmtKB(kb) {
  if (!kb) return '0 KB';
  if (kb < 1024)       return `${kb} KB`;
  if (kb < 1024 * 1024) return `${(kb / 1024).toFixed(1)} MB`;
  return `${(kb / 1024 / 1024).toFixed(2)} GB`;
}

// ─── Composants UI ────────────────────────────────────────────────────────

function SizeBar({ value, max, color = 'bg-orange-500' }) {
  const pct = max > 0 ? Math.min(100, Math.round((value / max) * 100)) : 0;
  return (
    <div className="flex items-center gap-2 flex-1">
      <div className="flex-1 h-1 bg-zinc-800 rounded-full overflow-hidden">
        <div className={`h-full rounded-full ${color} transition-all duration-500`} style={{ width: `${pct}%` }} />
      </div>
      <span className="text-[10px] text-zinc-600 w-10 text-right">{pct}%</span>
    </div>
  );
}

function Feedback({ msg, type, onClear }) {
  if (!msg) return null;
  const s = { success:'bg-emerald-950/30 border-emerald-800/30 text-emerald-300', error:'bg-red-950/30 border-red-800/30 text-red-300', warning:'bg-amber-950/30 border-amber-800/30 text-amber-300' };
  return (
    <div className={`flex items-center justify-between px-4 py-2.5 rounded border text-xs ${s[type]||s.error}`}>
      <span>{msg}</span>
      {onClear && <button onClick={onClear}><X size={12}/></button>}
    </div>
  );
}

// ─── Hook données backend ─────────────────────────────────────────────────

function useDiskData() {
  const [disk,    setDisk]    = useState(null);
  const [logs,    setLogs]    = useState([]);
  const [loading, setLoading] = useState(false);
  const [error,   setError]   = useState(null);

  const fetchAll = async () => {
    setLoading(true);
    setError(null);
    try {
      const [diskRes, logsRes] = await Promise.all([
        api.get('/superadmin/disk'),
        api.get('/superadmin/disk/logs'),
      ]);
      setDisk(diskRes.data);
      setLogs(logsRes.data.files || []);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  const truncateLog = async (filename) => {
    const r = await api.delete(`/superadmin/disk/logs/${encodeURIComponent(filename)}`);
    await fetchAll();
    return r.data;
  };

  useEffect(() => { fetchAll(); }, []);

  return { disk, logs, loading, error, fetchAll, truncateLog };
}

// ─── Panneau principal ────────────────────────────────────────────────────

export default function PanneauDisque() {
  // ── Store : disk gauge temps réel ────────────────────────────────────
  const { systemMetrics } = usePesacoreIH();
  const diskPct = typeof systemMetrics?.disk === 'number' && isFinite(systemMetrics.disk)
    ? systemMetrics.disk : 0;

  // ── Backend : détail ─────────────────────────────────────────────────
  const { disk, logs, loading, error, fetchAll, truncateLog } = useDiskData();
  const [feedback,    setFeedback]    = useState(null);
  const [collSearch,  setCollSearch]  = useState('');
  const [sortBy,      setSortBy]      = useState('storageSizeKB');

  const showFb = (type, msg) => { setFeedback({ type, msg }); setTimeout(() => setFeedback(null), 4000); };

  const dbSummary  = disk?.database?.summary;
  const collections = (disk?.database?.collections || [])
    .filter(c => !collSearch || c.name.includes(collSearch))
    .sort((a, b) => (b[sortBy] || 0) - (a[sortBy] || 0));
  const maxStorage  = collections[0]?.[sortBy] || 1;

  const dirs        = disk?.filesystem?.dirs || [];
  const totalDirLabel = disk?.filesystem?.totalLabel || '—';

  // couleur jauge disque
  const gaugeColor = diskPct >= 90 ? 'bg-red-500' : diskPct >= 75 ? 'bg-amber-500' : 'bg-emerald-500';
  const gaugeText  = diskPct >= 90 ? 'text-red-400' : diskPct >= 75 ? 'text-amber-400' : 'text-emerald-400';

  return (
    <div className="space-y-6 max-w-5xl mx-auto">

      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-black text-orange-300 tracking-tighter">STOCKAGE & DISQUE</h2>
          <p className="text-[10px] text-zinc-600 uppercase tracking-widest mt-0.5">
            DB MongoDB + filesystem serveur
          </p>
        </div>
        <button onClick={fetchAll} disabled={loading}
          className="p-2 bg-zinc-900 border border-zinc-700 rounded text-zinc-500 hover:text-zinc-300 disabled:opacity-40">
          <RefreshCw size={13} className={loading ? 'animate-spin' : ''} />
        </button>
      </div>

      {error && (
        <div className="px-4 py-3 bg-red-950/30 border border-red-800/30 rounded-xl text-red-300 text-xs flex gap-2">
          <AlertTriangle size={14} />{error}
        </div>
      )}

      <Feedback {...(feedback || {})} onClear={() => setFeedback(null)} />

      {/* ── Gauge disque temps réel — depuis le store socket ── */}
      <section className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <Server size={13} className="text-zinc-600" />
            <span className="text-[10px] text-zinc-500 uppercase tracking-widest">Disque système — temps réel</span>
          </div>
          <span className={`text-2xl font-black font-mono ${gaugeText}`}>
            {diskPct > 0 ? `${diskPct.toFixed(1)}%` : '—'}
          </span>
        </div>
        <div className="h-2 bg-zinc-800 rounded-full overflow-hidden">
          <div className={`h-full rounded-full transition-all duration-700 ${gaugeColor}`}
            style={{ width: `${Math.min(100, diskPct)}%` }} />
        </div>
        {diskPct >= 80 && (
          <p className="text-[10px] text-amber-400 flex items-center gap-1.5">
            <AlertTriangle size={10} />
            Espace disque élevé — envisager un nettoyage des logs ou une migration
          </p>
        )}
      </section>

      {/* ── DB MongoDB summary ── */}
      {dbSummary && (
        <section className="space-y-2">
          <p className="text-[10px] text-zinc-600 uppercase tracking-widest flex items-center gap-2">
            <Database size={11} /> MongoDB
          </p>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {[
              { label: 'Données',      value: fmtKB(dbSummary.dataKB) },
              { label: 'Stockage',     value: fmtKB(dbSummary.storageKB) },
              { label: 'Index',        value: fmtKB(dbSummary.indexKB) },
              { label: 'Collections',  value: dbSummary.collections },
            ].map(({ label, value }) => (
              <div key={label} className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl p-3 text-center">
                <div className="text-lg font-black text-zinc-200">{value}</div>
                <div className="text-[9px] text-zinc-600 uppercase tracking-widest mt-0.5">{label}</div>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* ── Collections détail ── */}
      {collections.length > 0 && (
        <section className="space-y-2">
          <div className="flex items-center gap-3 flex-wrap">
            <p className="text-[10px] text-zinc-600 uppercase tracking-widest flex items-center gap-2 flex-1">
              <Database size={11} /> Collections ({collections.length})
            </p>
            <input value={collSearch} onChange={e => setCollSearch(e.target.value)}
              placeholder="Filtrer..."
              className="w-36 bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-200 focus:border-orange-600/50 outline-none" />
            <select value={sortBy} onChange={e => setSortBy(e.target.value)}
              className="bg-zinc-900 border border-zinc-800 rounded px-2 py-1 text-xs text-zinc-400 outline-none">
              <option value="storageSizeKB">Trier par stockage</option>
              <option value="sizeKB">Trier par données</option>
              <option value="count">Trier par documents</option>
              <option value="indexSizeKB">Trier par index</option>
            </select>
          </div>

          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden">
            <div className="grid grid-cols-12 px-4 py-2 border-b border-zinc-800/40 text-[9px] text-zinc-600 uppercase tracking-widest">
              <span className="col-span-3">Collection</span>
              <span className="col-span-2 text-right">Docs</span>
              <span className="col-span-2 text-right">Données</span>
              <span className="col-span-2 text-right">Index</span>
              <span className="col-span-3">Taille relative</span>
            </div>
            <div className="divide-y divide-zinc-800/20 max-h-96 overflow-y-auto">
              {collections.map(c => (
                <div key={c.name} className="grid grid-cols-12 items-center px-4 py-2.5 hover:bg-zinc-800/10">
                  <span className="col-span-3 text-xs font-mono text-zinc-300 truncate" title={c.name}>{c.name}</span>
                  <span className="col-span-2 text-right text-xs text-zinc-500">{c.count.toLocaleString()}</span>
                  <span className="col-span-2 text-right text-[10px] text-zinc-500">{fmtKB(c.sizeKB)}</span>
                  <span className="col-span-2 text-right text-[10px] text-zinc-600">{fmtKB(c.indexSizeKB)}</span>
                  <div className="col-span-3 pl-3">
                    <SizeBar value={c[sortBy] || 0} max={maxStorage}
                      color={c.storageSizeKB > maxStorage * 0.5 ? 'bg-orange-500' : 'bg-zinc-600'} />
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ── Dossiers filesystem ── */}
      {dirs.length > 0 && (
        <section className="space-y-2">
          <p className="text-[10px] text-zinc-600 uppercase tracking-widest flex items-center gap-2">
            <FolderOpen size={11} /> Filesystem serveur · Total {totalDirLabel}
          </p>
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden">
            <div className="divide-y divide-zinc-800/20">
              {dirs.map(d => {
                const maxDir = Math.max(...dirs.map(x => x.sizeBytes || 0), 1);
                return (
                  <div key={d.key} className="flex items-center gap-4 px-4 py-3">
                    <FolderOpen size={13} className={d.exists ? 'text-orange-500/60' : 'text-zinc-700'} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-mono text-zinc-300">{d.label}</span>
                        <div className="flex items-center gap-3 text-[10px] text-zinc-600">
                          <span>{d.fileCount ?? 0} fichiers</span>
                          <span className="font-bold text-zinc-400">{d.exists ? `${d.sizeMB ?? 0} MB` : 'inexistant'}</span>
                        </div>
                      </div>
                      <SizeBar value={d.sizeBytes || 0} max={maxDir} color="bg-orange-600/60" />
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* ── Fichiers de log ── */}
      <section className="space-y-2">
        <p className="text-[10px] text-zinc-600 uppercase tracking-widest flex items-center gap-2">
          <FileText size={11} /> Logs serveur
        </p>
        {logs.length === 0 ? (
          <div className="text-center py-6 border border-zinc-800/40 rounded-xl text-zinc-700 text-xs">
            {loading ? 'Chargement...' : 'Aucun fichier de log'}
          </div>
        ) : (
          <div className="bg-zinc-900/40 border border-zinc-800/60 rounded-xl overflow-hidden">
            <div className="divide-y divide-zinc-800/20">
              {logs.map(f => (
                <div key={f.name} className="flex items-center gap-3 px-4 py-3 group">
                  <FileText size={13} className="text-zinc-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-mono text-zinc-300">{f.name}</p>
                    <p className="text-[10px] text-zinc-600">
                      {f.sizeLabel} · Modifié {new Date(f.modifiedAt).toLocaleDateString('fr-FR')}
                    </p>
                  </div>
                  <button
                    onClick={async () => {
                      if (!window.confirm(`Vider "${f.name}" ?`)) return;
                      try {
                        await truncateLog(f.name);
                        showFb('success', `${f.name} vidé`);
                      } catch (err) {
                        showFb('error', err.response?.data?.error || err.message);
                      }
                    }}
                    className="opacity-0 group-hover:opacity-100 flex items-center gap-1.5 px-2.5 py-1.5 bg-zinc-800 border border-zinc-700 hover:border-red-700/40 rounded text-zinc-500 hover:text-red-400 text-[10px] transition-all">
                    <Trash2 size={11} />
                    Vider
                  </button>
                </div>
              ))}
            </div>
          </div>
        )}
      </section>

    </div>
  );
}